import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import { UPDATE_USER_BY_ADMIN } from "@/hooks";

interface UseUpdateUserReturn {
  updateUser: (_variables?: any) => Promise<any>;
  loading: boolean;
  success: boolean;
  resetForm: boolean;
}

/**
 * Custom hook to update a user.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 */
export const useUpdateUser = (
  onCompleted?: () => void,
  onError?: (_error: string) => void,
): UseUpdateUserReturn => {
  const _onCompleted = onCompleted || (() => {});
  const _onError = onError || (() => {});
  const [success, setSuccess] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  const [updateUser, { loading }] = useMutation<any>(UPDATE_USER_BY_ADMIN, {
    refetchQueries: [
      "AllUsers",
      "SystemUsers",
      "PartnerUsers",
      "TenantsUsersList",
    ],
    onCompleted: (response) => {
      const res =
        (response as any).updateUserAdmin ||
        (response as any).comUpdateUserAdmin;
      if (res?.success) {
        setSuccess(true);
        setResetForm(true);
        enqueueSnackbar("User updated successfully", {
          variant: "success",
        });
        setTimeout(() => {
          setSuccess(false);
          setResetForm(false);
        }, 5000);
        _onCompleted();
      } else {
        setSuccess(false);
        enqueueSnackbar("Failed to update user", {
          variant: "error",
        });
      }
    },
    onError: ({ message }: any) => {
      setSuccess(false);
      enqueueSnackbar(message || "An error occurred while updating user", {
        variant: "error",
      });
      _onError(message);
    },
  });

  return {
    updateUser,
    loading,
    success,
    resetForm,
  };
};
